import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LimiteDeErro } from '../componentes/LimiteDeErro';
import {
  Aviso,
  Botao,
  CampoDeSelecao,
  Cartao,
  EstadoCarregando,
  EstadoVazio,
  Selo,
} from '../componentes/ui';
import { useEu } from '../hooks/useEu';
import {
  useAutenticado,
  useEmpresaCorrente,
  useEscolherEmpresa,
  useIdentidade,
  useSair,
} from '../hooks/useSessao';
import { mensagemDoErro } from '../servicos/erros';
import {
  nomeDaEmpresa,
  razaoSocialComplementar,
  type EmpresaDoUsuario,
  type Eu,
} from '../servicos/eu';
import type { PapelUsuario, VinculoDoToken } from '../servicos/jwt';
import { destinoDeOrigem } from './Login';

/**
 * RN-603 — Seleção explícita de empresa.
 *
 * ## O que esta tela corrige
 *
 * O legado fazia `setCurrentBusiness(business[0])` em todo caminho de login. Não
 * havia seletor: a primeira empresa do array vencia, e quem tinha dois vínculos
 * via metade dos dados sem nenhum sinal de que faltava algo. Não era um erro
 * visível — era um dado ausente com aparência de dado completo, que é pior.
 *
 * Com um único vínculo a tela nem aparece (`empresaCorrente` já veio resolvida).
 * Com mais de um, ela é obrigatória antes de qualquer número. Depois do login,
 * a troca é pela janela "Trocar empresa" (`layout/TrocarEmpresa.tsx`), que usa
 * a mesma lista.
 *
 * ## De onde vem o nome
 *
 * De `GET /api/auth/eu`, não do token: o JWT carrega só ids porque é assinado e
 * não criptografado, e razão social é dado da empresa (`servicos/eu.ts`). Exibe
 * o **nome fantasia** com reserva na razão social — D-009: a operadora tem razão
 * social "MECA" e fantasia "MS3 Capital", e a razão social mostraria ao
 * contribuinte um nome que ele não reconhece.
 *
 * Se a chamada falhar, a tela **continua utilizável** com os ids do token e diz
 * que são ids. Escolher empresa é o caminho crítico do login inteiro; travá-lo
 * porque um endpoint de nomes caiu trocaria um rótulo feio por uma sessão presa.
 */
export function SelecionarEmpresa(props: { readonly destino?: string }): JSX.Element {
  const autenticado = useAutenticado();
  const identidade = useIdentidade();
  const empresaCorrente = useEmpresaCorrente();
  const escolherEmpresa = useEscolherEmpresa();
  const eu = useEu();
  const logout = useSair();
  // O login manda para cá o destino original (`state.de`), já validado lá.
  const local = useLocation();

  if (!autenticado) return <Navigate to="/entrar" replace />;
  if (empresaCorrente !== null) {
    return <Navigate to={destinoDeOrigem(local.state) ?? props.destino ?? '/painel'} replace />;
  }

  const vinculos = identidade?.vinculos ?? [];

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-2xl flex-col justify-center px-6 py-12">
      <LimiteDeErro area="a seleção de empresa">
        <Cartao
          como="section"
          titulo="Escolha a empresa"
          subtitulo="Sua conta está vinculada a mais de uma empresa. Tudo que você vê a seguir é do vínculo escolhido aqui."
        >
          {vinculos.length === 0 ? (
            <EstadoVazio
              titulo="Nenhuma empresa vinculada"
              descricao="Esta conta está ativa, mas não tem vínculo com nenhuma empresa. Peça ao administrador da sua empresa para incluir seu acesso."
              comMoldura={false}
              acao={
                <Botao variante="secundario" onClick={() => logout.mutate()}>
                  Sair
                </Botao>
              }
            />
          ) : eu.isPending ? (
            <EstadoCarregando contexto="as empresas da sua conta" />
          ) : (
            <EscolhaDeEmpresa
              rotuloDoBotao="Entrar"
              itens={itensParaEscolha(eu.data, vinculos, identidade?.isAdmin === true)}
              // O nome é enfeite; a lista é o caminho crítico. A falha aparece,
              // a escolha continua possível.
              erroDeNomes={eu.isError ? mensagemDoErro(eu.error) : null}
              aoEscolher={escolherEmpresa}
              // Lista vazia não pode ser beco sem saída: sem opção, sai-se.
              acaoSeVazia={
                <Botao variante="secundario" onClick={() => logout.mutate()}>
                  Sair
                </Botao>
              }
            />
          )}

          {logout.isError && (
            <div className="mt-4">
              <Aviso tom="erro" titulo="A sessão foi encerrada neste dispositivo">
                O servidor não confirmou a revogação: {mensagemDoErro(logout.error)}
              </Aviso>
            </div>
          )}
        </Cartao>
      </LimiteDeErro>
    </main>
  );
}

/** Uma empresa como a tela precisa dela: id sempre, nome quando se sabe. */
export interface ItemDeEscolha {
  readonly businessId: string;
  /** `null` quando `/auth/eu` não respondeu — a tela mostra o id e diz o que é. */
  readonly nome: string | null;
  /** Razão social, só quando acrescenta algo ao nome exibido (D-009). */
  readonly razaoSocial: string | null;
  readonly papeis: readonly PapelUsuario[];
  /** Empresa desativada no cadastro — só chega à lista para admin. */
  readonly desativada: boolean;
}

/**
 * Junta o que o token garante com o que o endpoint enriquece.
 *
 * A lista de referência é a do `/auth/eu`, que separa as empresas desativadas.
 * Para quem não é admin, vínculo com empresa desativada não vira botão. Para o
 * admin vira, marcado: o que ele administra não depende daquela empresa, e um
 * admin cuja única empresa foi desativada ficava sem como entrar no sistema.
 *
 * Sem a resposta, cai para os vínculos do token: menos informação, mesma função.
 */
export function itensParaEscolha(
  eu: Eu | undefined,
  vinculos: readonly VinculoDoToken[],
  incluirDesativadas: boolean,
): readonly ItemDeEscolha[] {
  if (eu === undefined) {
    return vinculos.map((vinculo) => ({
      businessId: vinculo.businessId,
      nome: null,
      razaoSocial: null,
      papeis: vinculo.papeis,
      desativada: false,
    }));
  }

  const item =
    (desativada: boolean) =>
    (empresa: EmpresaDoUsuario): ItemDeEscolha => ({
      businessId: empresa.businessId,
      nome: nomeDaEmpresa(empresa),
      razaoSocial: razaoSocialComplementar(empresa),
      papeis: empresa.papeis,
      desativada,
    });

  return [
    ...eu.empresas.map(item(false)),
    ...(incluirDesativadas ? (eu.empresasDesativadas ?? []).map(item(true)) : []),
  ];
}

/**
 * A escolha de empresa — na tela do login e na janela "Trocar empresa" da
 * barra superior.
 *
 * ## Um campo de seleção, não uma lista de cartões
 *
 * Com sete, dez empresas, os cartões enchiam a tela e empurravam o resto para
 * baixo da dobra. O seletor ocupa uma linha, em ordem alfabética — como se
 * procura um nome —, e o que distinguia cada cartão (papel, "atual",
 * "desativada") aparece só para a empresa selecionada, logo abaixo.
 *
 * A troca só acontece no botão, não ao mudar o seletor: rolar a lista com o
 * teclado não pode ir trocando de empresa a cada seta.
 */
export function EscolhaDeEmpresa(props: {
  readonly itens: readonly ItemDeEscolha[];
  readonly erroDeNomes: string | null;
  readonly aoEscolher: (businessId: string) => boolean;
  /** A empresa em uso: vem selecionada e não é oferecida como troca. */
  readonly atual?: string | null;
  /** "Entrar" no login, "Trocar" na janela. */
  readonly rotuloDoBotao: string;
  /** O que oferecer quando não sobra empresa nenhuma — na tela do login, "Sair". */
  readonly acaoSeVazia?: React.ReactNode;
}): JSX.Element {
  const [escolhida, setEscolhida] = useState(props.atual ?? '');

  if (props.itens.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma empresa ativa"
        descricao="As empresas vinculadas a esta conta estão desativadas no cadastro. Fale com o administrador da sua empresa ou com a MS3 Capital."
        comMoldura={false}
        {...(props.acaoSeVazia === undefined ? {} : { acao: props.acaoSeVazia })}
      />
    );
  }

  const ordenados = [...props.itens].sort((a, b) =>
    rotuloDoItem(a).localeCompare(rotuloDoItem(b), 'pt-BR'),
  );
  const selecionado = props.itens.find((i) => i.businessId === escolhida) ?? null;
  const podeConfirmar = selecionado !== null && selecionado.businessId !== props.atual;

  return (
    <div className="flex flex-col gap-4">
      {props.erroDeNomes !== null && (
        <Aviso tom="atencao" titulo="Não foi possível carregar os nomes das empresas">
          {props.erroDeNomes} A lista mostra o identificador de cada empresa — não inventamos o
          nome. Você pode escolher normalmente.
        </Aviso>
      )}

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (podeConfirmar) props.aoEscolher(selecionado.businessId);
        }}
      >
        <CampoDeSelecao
          rotulo="Empresa"
          className="min-w-0 flex-1"
          value={escolhida}
          onChange={(e) => setEscolhida(e.target.value)}
          opcoes={[
            // Sem empresa em uso, nada vem pré-escolhido: a escolha é da pessoa.
            ...(props.atual == null ? [{ valor: '', rotulo: 'Selecione a empresa…' }] : []),
            ...ordenados.map((item) => ({
              valor: item.businessId,
              rotulo: `${rotuloDoItem(item)}${item.businessId === props.atual ? ' (atual)' : ''}${item.desativada ? ' — desativada' : ''}`,
            })),
          ]}
        />
        <Botao type="submit" disabled={!podeConfirmar}>
          {props.rotuloDoBotao}
        </Botao>
      </form>

      {selecionado !== null && (
        <div className="flex flex-wrap items-center gap-2">
          {selecionado.razaoSocial !== null && (
            <span className="text-xs text-conteudo-secundario">{selecionado.razaoSocial}</span>
          )}
          {selecionado.businessId === props.atual && <Selo tom="informativo">Atual</Selo>}
          {selecionado.desativada && <Selo tom="atencao">Desativada</Selo>}
          {selecionado.papeis.length === 0 ? (
            <Selo tom="atencao">sem papel definido</Selo>
          ) : (
            selecionado.papeis.map((papel) => (
              <Selo key={papel} semPonto>
                {rotuloDoPapel(papel)}
              </Selo>
            ))
          )}
        </div>
      )}

      {selecionado?.desativada === true && (
        <Aviso tom="atencao" titulo="Empresa desativada no cadastro">
          Como administrador, você pode entrar por ela: o que você administra não depende dessa
          empresa. Para reativá-la, use &quot;Nova empresa&quot; em Empresas com o CNPJ dela — o
          sistema oferece reativar o cadastro.
        </Aviso>
      )}
    </div>
  );
}

/** O nome exibido, ou o id quando o `/auth/eu` não respondeu. */
function rotuloDoItem(item: ItemDeEscolha): string {
  return item.nome ?? item.businessId;
}

/** RN-413 — Gestor edita e gerencia; Colaborador só visualiza. */
export function rotuloDoPapel(papel: PapelUsuario): string {
  const rotulos: Record<PapelUsuario, string> = {
    ADMIN: 'Administrador',
    STAFF: 'Equipe MS3',
    BUSINESS_OWNER: 'Gestor',
    BUSINESS_EMPLOYER: 'Colaborador',
  };
  return rotulos[papel];
}
